import { Button, Modal, Space, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getAPI } from 'requests/api';
import { getAdminReportFileDownload } from 'services/report';
import { getTenant } from 'services/tenants';
import { getUserId } from 'services/auth';
import { useCallback } from 'react';
import { useCanMultiple } from 'components/layout/sider';
import { useCorporateReportExport } from 'pages/corporate_reports/hooks/useCorporateReportExport';
import { useNotification } from '@refinedev/core';

// O atlas gera um lote por vez no servidor; o relatório que fica em fila por mais
// que isto é tratado como falha aqui, em vez de deixar o botão girando para sempre.
export const BULK_DOCUMENTS_MAX_AGE_MS = 60 * 60 * 1000;

// Um relatório em andamento por agente e por tenant: no mesmo navegador, outra
// pessoa (ou outro tenant) não herda o "gerando" de quem estava antes.
export const bulkDocumentsStorageKey = () =>
  `bulk_course_documents:${getTenant().project}:${getUserId() ?? 'anonimo'}`;

export const createBulkCourseDocumentsReport = async (courseId: string) => {
  const response = await getAPI().post<{ id: string }>('/admin/reports', {
    kind: 'admin_bulk_course_documents',
    inputs: { course_id: courseId }
  });

  return { id: response.data.id };
};

interface BulkDocumentsDownloadProps {
  courseId?: string;
  courseName?: string;
}

/**
 * GERE6-534: baixa num ZIP os documentos aprovados das matrículas ativas do
 * curso escolhido no filtro. Mesma regra do atlas (`DocumentationPolicy#bulk_download?`):
 * quem lista as documentações e já abre os arquivos um a um.
 */
export const BulkDocumentsDownload = ({
  courseId,
  courseName
}: BulkDocumentsDownloadProps) => {
  const notification = useNotification();
  const permissions = useCanMultiple([
    { resource: 'documentations', action: 'list' },
    { resource: 'documentations', action: 'show' },
    { resource: 'documents', action: 'show' }
  ]);
  const canDownload =
    Boolean(permissions[0]?.data?.can) &&
    (Boolean(permissions[1]?.data?.can) || Boolean(permissions[2]?.data?.can));

  const { reportId, handleExportReport, cancel } = useCorporateReportExport({
    storageKey: bulkDocumentsStorageKey(),
    searchFormProps: {},
    notification,
    createReport: () => createBulkCourseDocumentsReport(courseId!),
    // O ZIP traz RG, CPF e diplomas: só pela rota autenticada, nunca pela pública.
    downloadFile: getAdminReportFileDownload,
    maxAgeMs: BULK_DOCUMENTS_MAX_AGE_MS,
    showErrorLog: true
  });
  const isGenerating = Boolean(reportId);

  const handleClick = useCallback(() => {
    Modal.confirm({
      title: 'Baixar documentos do curso',
      icon: <DownloadOutlined />,
      content: `Você vai baixar, num arquivo ZIP, os documentos aprovados das matrículas ativas ${
        courseName ? `de "${courseName}"` : 'do curso selecionado'
      }, uma pasta por aluno, com um index.csv de quem entrou e do que faltou. A geração pode levar alguns minutos — você pode fechar esta página, o download começará automaticamente quando estiver pronto.`,
      okText: 'Gerar arquivo',
      cancelText: 'Cancelar',
      onOk: () => handleExportReport()
    });
  }, [courseName, handleExportReport]);

  if (!canDownload) {
    return null;
  }

  const tooltip = isGenerating
    ? 'Um arquivo de documentos está sendo gerado'
    : courseId
    ? ''
    : 'Escolha um curso no filtro';

  return (
    <Space>
      {isGenerating && (
        <Tooltip title="Para de esperar aqui. O arquivo que já estava sendo gerado não é baixado.">
          <Button type="link" onClick={cancel}>
            Cancelar
          </Button>
        </Tooltip>
      )}
      <Tooltip title={tooltip}>
        <Button
          icon={<DownloadOutlined />}
          loading={isGenerating}
          disabled={!courseId || isGenerating}
          onClick={handleClick}
        >
          Baixar documentos do curso
        </Button>
      </Tooltip>
    </Space>
  );
};
